import type { CSSProperties, ReactNode } from 'react'
import { useNavigate } from 'react-router'

const KEYWORDS = [
  'Canvas',
  'MCP',
  'Structured Output',
  'Agentic Workflow',
  'Persistent Resource',
  'Token-Efficient',
  'Human-in-the-Loop',
  'Agent-Native',
]

const codePillStyle: CSSProperties = {
  padding: '2px 6px',
  background: 'var(--bg-1)',
  borderRadius: 4,
}

export default function Agents() {
  const navigate = useNavigate()

  return (
    <div style={{ padding: '64px 0 96px' }}>
      <div className="container" style={{ maxWidth: 780 }}>

        <div className="eyebrow" style={{ marginBottom: 16 }}>
          <span className="num">~</span>agents
        </div>
        <h1
          className="h-display"
          style={{ fontSize: 'clamp(36px, 5.5vw, 68px)', marginBottom: 24, lineHeight: 1.05 }}
        >
          The grid canvas<br />
          <span className="accent">for AI agents.</span>
        </h1>
        <p className="lede" style={{ marginBottom: 16, maxWidth: 640 }}>
          Agent-generated data is starting to dwarf the data humans produce by hand.
          The chat window was never the right place to put it. Instadash is.
        </p>
        <p className="muted" style={{ fontSize: 14, marginBottom: 72 }}>
          A short manifesto in four parts — why we built this, and what it's for.
        </p>

        <Section num="01" eyebrow="the shift" title="Infrastructure, not a tool.">
          <p>
            For thirty years, structured data had two destinations: a database a human
            queried, or a spreadsheet a human edited. In 2026 a third actor showed up
            and started producing more rows per hour than either of them — agents.
          </p>
          <p>
            Agents scrape, enrich, classify, summarise, label, score, and rank. The
            output is structured. The destination, historically, has been a markdown
            table glued into the bottom of a chat message. That doesn't scale, doesn't
            persist, doesn't reuse, and doesn't compose. Building yet another database
            for it doesn't either — agents need a target that's as fast to write to as
            {' '}<code className="mono" style={codePillStyle}>console.log</code>{' '}
            and as durable as a permalink.
          </p>
          <p>
            Instadash is that target. One <code className="mono" style={codePillStyle}>push</code>,
            one URL, schema inferred, versioned forever, queryable by the agent that
            made it and by every agent that comes after.
          </p>
        </Section>

        <Section num="02" eyebrow="ux friction" title="Chat windows aren't spreadsheets.">
          <p>
            A markdown table with eighty rows is a bad experience for everyone in the
            room. The human can't filter, sort, or copy it cleanly. The agent burned
            half its context window painting cells. The data is dead the moment the
            conversation ends.
          </p>
          <p>
            An agent that needs to show structured data should publish it and return a
            link. The reader gets a real grid — search, filter, inspect, edit. The
            agent gets its context back. The artifact survives the session.
          </p>
        </Section>

        <Section num="03" eyebrow="token economy" title="Move the rows out of the prompt.">
          <p>
            Every row of data an agent carries in its context is a row it pays for on
            every turn. Five hundred rows of competitor research is fifteen thousand
            tokens that get re-billed at every reasoning step until the conversation
            ends. The same rows, sitting on a grid URL, are zero tokens — fetched on
            demand, paginated, schema-summarised.
          </p>
          <p>
            <code className="mono" style={codePillStyle}>instadash_push</code>{' '}
            takes structured output out of the prompt.{' '}
            <code className="mono" style={codePillStyle}>instadash_read</code>{' '}
            puts only the rows the agent needs back in. That's an order of magnitude
            of token spend on any non-trivial agent workflow.
          </p>
        </Section>

        <Section num="04" eyebrow="the hand-off" title="Where the agent stops, the human starts.">
          <p>
            Most useful agent workflows have a human in the loop. The agent finds
            two hundred prospects; a human picks the forty real ones. The agent extracts
            claims from a hundred PDFs; a human verifies the dozen that matter. That
            review needs a real surface — checkboxes, edits, comments — not a
            re-prompt-the-agent loop.
          </p>
          <p>
            A grid is the bridge. The agent writes, the human reads and edits, the
            agent reads back what changed via{' '}
            <code className="mono" style={codePillStyle}>instadash_edits</code> —
            and proceeds with confidence about what was approved. The loop closes in
            two MCP calls instead of two thousand tokens of clarifying chat.
          </p>
        </Section>

        <div
          className="card card-pad"
          style={{ marginBottom: 72, borderColor: 'var(--accent-line, var(--line))' }}
        >
          <div className="eyebrow" style={{ marginBottom: 12 }}>positioning, said out loud</div>
          <p style={{ marginBottom: 16, fontSize: 14, color: 'var(--fg-1)' }}>
            We use the same eight words everywhere — site copy, docs, READMEs, MCP
            descriptions. Not because we're chasing a keyword. Because when a developer
            asks a 2026 model <em>"where should my agent put structured output?"</em>,
            we want the model to have the answer ready. Distribution to humans is now
            distribution to the models humans ask.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {KEYWORDS.map(k => (
              <span
                key={k}
                className="chip mono"
                style={{ fontSize: 11, padding: '4px 10px' }}
              >
                {k}
              </span>
            ))}
          </div>
        </div>

        <Section eyebrow="where this becomes real" title="Three doors into the same system.">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: 12,
            }}
          >
            <DoorCard
              label="MCP server"
              blurb="Paste one URL into Claude, Cursor, or Windsurf. Your grids are live in 30 seconds."
              onClick={() => navigate('/mcp')}
            />
            <DoorCard
              label="Recipes"
              blurb="Framework-by-framework integration guides — Firecrawl, LangGraph, CrewAI, PydanticAI."
              onClick={() => navigate('/recipes')}
            />
            <DoorCard
              label="Docs"
              blurb="The CLI, the HTTP API, the schema rules, the versioning model."
              onClick={() => navigate('/docs')}
            />
          </div>
        </Section>

        <div
          className="card card-pad"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}
        >
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>Give your agents a real surface.</div>
            <p className="muted" style={{ fontSize: 13 }}>
              Free tier, no credit card. Public grids stay public forever.
            </p>
          </div>
          <button
            className="btn btn-primary btn-lg"
            style={{ flexShrink: 0 }}
            onClick={() => navigate('/get-started')}
          >
            Get started →
          </button>
        </div>

      </div>
    </div>
  )
}

function Section({
  num,
  eyebrow,
  title,
  children,
}: {
  num?: string
  eyebrow: string
  title: string
  children: ReactNode
}) {
  return (
    <section style={{ marginBottom: 72 }}>
      <div className="eyebrow" style={{ marginBottom: 14 }}>
        {num && <><span className="num">{num}</span> — </>}{eyebrow}
      </div>
      <h2
        className="h-display"
        style={{ fontSize: 'clamp(26px, 3vw, 34px)', marginBottom: 18, lineHeight: 1.15 }}
      >
        {title}
      </h2>
      <div style={{ fontSize: 16, color: 'var(--fg-1)', lineHeight: 1.7, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {children}
      </div>
    </section>
  )
}

function DoorCard({ label, blurb, onClick }: { label: string; blurb: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="card card-pad card-hover"
      style={{
        textAlign: 'left',
        background: 'transparent',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div className="mono" style={{ fontSize: 12, color: 'var(--accent)' }}>{label} →</div>
      <div style={{ fontSize: 13, color: 'var(--fg-2)', lineHeight: 1.55 }}>{blurb}</div>
    </button>
  )
}
